// AnmeldenPage.jsx — die EINE Anmeldeseite (Aufgabe 132 vom 02.10.2026, zusammengeführt 18.10.2026)
//
// Bis 18.10.2026 gab es drei Wege: /login (Plakatwand + LOGIN-Fenster mit
// Vereins- und Athleten-Reiter), das LOGIN-Fenster der Navigationsleiste und
// diese Seite. Jetzt ist es eine Seite; /login und die alten Personal-
// Adressen leiten hierher (App.js), der LOGIN-Knopf führt hierher.
//
// Die Seite erkennt die Kontoart selbst, in dieser Reihenfolge:
//   1. Turnierpersonal (/api/anmeldung/personal: Personalkonto und die alten
//      Kampfrichter-, Registrierungs- und Waage-Konten in EINEM Aufruf).
//      Zuerst, weil /api/auth/login einen unbekannten Namen an die
//      Dojosoftware weiterreicht — Kampfrichter-Passwörter gehören dort nicht hin.
//   2. Verwaltung und Vereine (/api/auth/login, mit Zwei-Faktor).
//   3. Athleten mit E-Mail (/api/athlete/login), nur wenn 1 und 2 nichts finden.
// Alle Anmeldungen teilen sich die Versuchsbremse (authLimiter); so zählt ein
// Personal-Login einmal, ein Vereins-Login zweimal.
//
// Danach je Rolle: Admin → /dashboard, Verein → /verein (oder zur Anmeldung
// des Turniers aus ?turnier=), Athlet → /athlete/dashboard, Personal → am
// Turniertag mit genau einer Aufgabe direkt dorthin (Leitstelle, Kampfrichter-
// App an der eigenen Matte, Waage, Check-in), sonst die Übersicht /staff.
import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { vereinsAnmeldungSpeichern, personalAnmeldungSpeichern } from '../lib/anmeldungSpeichern';
import { einsatzOeffnen } from '../lib/personalEinsatz';
import { zielZusatz, zielNachVereinsLogin } from '../lib/anmeldeZiel';
import './AnmeldenPage.css';

const SYMBOL = { admin: '🛡️', verein: '🏫', konto: '🔐', kampfrichter: '⚖️', personal: '👥', registrierung: '📝', waage: '⚖' };

async function post(url, body) {
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return { status: r.status, d: await r.json().catch(() => ({})) };
  } catch { return { status: 0, d: {} }; }
}

const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const tagVon = (datum) => {
  if (!datum) return null;
  const d = new Date(datum);
  return Number.isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Wohin nach dem Personal-Login? Nur am Turniertag und nur bei genau einer
 * Aufgabe mit eigener Seite direkt dorthin — sonst die Übersicht.
 */
async function personalZiel(token) {
  try {
    const r = await fetch('/api/personal/mein-einsatz', { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || !d.success) return '/staff';
    const seiten = ['turnierleitung', 'kampfrichter', 'waage', 'registrierung'];
    // `zugang` (Server): Hauptkampfrichter & Co. zählen als Kampfrichter, Anmeldung als Registrierung
    const heuteDa = (d.data || []).filter((z) => tagVon(z.datum) === heute() && seiten.includes(z.zugang || z.rolle));
    if (heuteDa.length !== 1) return '/staff';
    const z = heuteDa[0];
    const zugang = z.zugang || z.rolle;
    if (zugang === 'turnierleitung') return `/leitstelle/${z.turnier_id}`;
    return await einsatzOeffnen(z.turnier_id, zugang);
  } catch {
    return '/staff';
  }
}

export default function AnmeldenPage({ onLogin }) {
  const navigate = useNavigate();
  const [suche] = useSearchParams();
  const turnierZiel = suche.get('turnier') || suche.get('turnierId');
  const eventZiel = suche.get('event');
  const [name, setName] = useState(() => suche.get('name') || '');
  const [passwort, setPasswort] = useState('');
  const [zeigen, setZeigen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [auswahl, setAuswahl] = useState(null);       // mehrere Konten passen
  const [zwischenToken, setZwischenToken] = useState(null);
  const [code, setCode] = useState('');

  const vereinFertig = (data) => {
    const userData = vereinsAnmeldungSpeichern(data, name.trim());
    const admin = userData.user.role === 'admin';
    // Verein kam von einem Turnier oder einer Veranstaltung: direkt zur Anmeldung dort
    if (!admin && (turnierZiel || eventZiel)) { navigate(zielNachVereinsLogin({ turnier: turnierZiel, event: eventZiel }), { replace: true }); return; }
    if (onLogin) onLogin(userData);          // App.handleLogin → /dashboard bzw. /verein
    else navigate(admin ? '/dashboard' : '/verein', { replace: true });
  };

  const waehlen = async (o) => {
    setFehler('');
    if (o.art === 'admin' || o.art === 'verein' || o.art === 'konto') {
      if (o.daten.zwei_faktor) { setZwischenToken(o.daten.zwischen_token); setAuswahl(null); return; }
      vereinFertig(o.daten);
      return;
    }
    const ziel = personalAnmeldungSpeichern(o);
    if (o.art === 'personal') {
      setLaedt(true);
      const weiter = await personalZiel(o.token);
      setLaedt(false);
      navigate(weiter, { replace: true });
      return;
    }
    navigate(ziel, { replace: true });
  };

  const absenden = async (e) => {
    e.preventDefault();
    setLaedt(true); setFehler(''); setAuswahl(null);
    const eingabe = name.trim();
    const optionen = [];
    const antworten = [];

    // 1. Turnierpersonal
    const p = await post('/api/anmeldung/personal', { benutzername: eingabe, passwort });
    antworten.push(p);
    if (p.status === 200 && p.d.success) optionen.push(...(p.d.treffer || []));

    // 2. Verwaltung / Verein — nur, wenn kein Personal-Konto passt
    if (!optionen.length && p.status !== 429) {
      const a = await post('/api/auth/login', { email: eingabe, passwort });
      antworten.push(a);
      if (a.status === 200 && a.d.success) {
        // Mit zweitem Faktor verrät die Antwort die Rolle noch nicht: neutral beschriften
        const rolle = a.d.zwei_faktor ? 'konto' : (a.d.user?.role === 'admin' ? 'admin' : 'verein');
        const TITEL = { admin: 'Turnierverwaltung (Admin)', verein: 'Vereinsbereich', konto: 'Verwaltung / Verein' };
        optionen.push({ art: rolle, titel: TITEL[rolle], name: a.d.user?.name || a.d.name || eingabe, daten: a.d });
      }
      // 3. Athlet (nur mit E-Mail-Adresse)
      if (!optionen.length && a.status !== 429 && eingabe.includes('@')) {
        const at = await post('/api/athlete/login', { email: eingabe, passwort });
        antworten.push(at);
        if (at.status === 200 && at.d.success) {
          localStorage.setItem('athleteToken', at.d.token);
          localStorage.setItem('athleteData', JSON.stringify(at.d.athlete));
          setLaedt(false);
          navigate('/athlete/dashboard', { replace: true });
          return;
        }
      }
    }
    setLaedt(false);
    if (optionen.length === 1) return waehlen(optionen[0]);
    if (optionen.length > 1) return setAuswahl(optionen);
    if (antworten.some((x) => x.status === 429)) return setFehler('Zu viele Versuche. Bitte 15 Minuten warten.');
    if (antworten.every((x) => x.status === 0)) return setFehler('Keine Verbindung. Bitte erneut versuchen.');
    // Eine sprechende Sperre (Konto aus, Turnier vorbei, Konto gesperrt) vor „falsch“
    const gesperrt = antworten.find((x) => x.status === 403 || x.status === 423);
    setFehler(gesperrt?.d.error || 'Benutzername oder Passwort stimmt nicht.');
  };

  const codeSenden = async (e) => {
    e.preventDefault();
    setLaedt(true); setFehler('');
    const r = await post('/api/auth/2fa', { zwischen_token: zwischenToken, code: code.trim() });
    setLaedt(false);
    if (r.status === 200 && r.d.success) return vereinFertig(r.d);
    setFehler(r.d.error || 'Code stimmt nicht.');
    if (r.status === 401 && /abgelaufen|Ungültige Anmeldung/i.test(r.d.error || '')) setZwischenToken(null);
  };

  return (
    <div className="anm-seite">
      <div className="anm-karte">
        <div className="anm-kopf">
          <Link to="/" className="anm-logo-link" aria-label="Zur Startseite">
            <img src="/logo.webp" alt="TDA" className="anm-logo" width="64" height="64" />
          </Link>
          <span className="anm-marke">TDA Tournament Center</span>
          <h1>Anmelden</h1>
          <p>Eine Anmeldung für Verwaltung, Vereine, Athleten und Turnierpersonal.</p>
          {(turnierZiel || eventZiel) && <p className="anm-ziel">Danach geht es direkt zur Anmeldung für die gewählte Veranstaltung.</p>}
        </div>
        {/* Wer vom Turnier kommt und noch kein Konto hat, sieht den Weg sofort (25.10.2026) */}
        {turnierZiel && !zwischenToken && !auswahl && (
          <Link className="anm-erstmal" to={`/register${zielZusatz({ turnier: turnierZiel })}`}>
            <strong>Zum ersten Mal hier?</strong> Verein kostenlos registrieren
          </Link>
        )}

        {zwischenToken ? (
          <form onSubmit={codeSenden} className="anm-form">
            <p className="anm-hinweis">Dein Konto ist mit einem zweiten Faktor geschützt. Bitte den Code aus der Authenticator-App eingeben. Ein Wiederherstellungs-Code geht an derselben Stelle.</p>
            <label className="anm-feld">Code
              <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus required inputMode="text" autoComplete="one-time-code" />
            </label>
            {fehler && <div className="anm-fehler" role="alert">{fehler}</div>}
            <button className="anm-knopf" disabled={laedt}>{laedt ? 'Wird geprüft…' : 'Bestätigen'}</button>
            <button type="button" className="anm-link" onClick={() => { setZwischenToken(null); setCode(''); }}>Zurück</button>
          </form>
        ) : auswahl ? (
          <div className="anm-form">
            <p className="anm-hinweis">Name und Passwort passen auf mehrere Zugänge. Als was möchtest du dich anmelden?</p>
            {auswahl.map((o) => (
              <button key={o.art} className="anm-wahl" onClick={() => waehlen(o)}>
                <span className="anm-wahl-symbol">{SYMBOL[o.art] || '•'}</span>
                <span><strong>{o.titel}</strong><br /><span className="anm-grau">{o.name}</span></span>
              </button>
            ))}
            <button type="button" className="anm-link" onClick={() => setAuswahl(null)}>Zurück</button>
          </div>
        ) : (
          <form onSubmit={absenden} className="anm-form">
            <label className="anm-feld">Benutzername oder E-Mail
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="username" autoCapitalize="none"
                spellCheck={false} required autoFocus={!name} />
            </label>
            <label className="anm-feld">Passwort
              <span className="anm-pw">
                <input type={zeigen ? 'text' : 'password'} value={passwort} onChange={(e) => setPasswort(e.target.value)}
                  autoComplete="current-password" required autoFocus={!!name} />
                <button type="button" className="anm-pw-zeigen" onClick={() => setZeigen((z) => !z)}
                  aria-label={zeigen ? 'Passwort verbergen' : 'Passwort anzeigen'}>{zeigen ? 'Verbergen' : 'Zeigen'}</button>
              </span>
            </label>
            {fehler && <div className="anm-fehler" role="alert">{fehler}</div>}
            <button className="anm-knopf" disabled={laedt}>{laedt ? 'Wird geprüft…' : 'Anmelden'}</button>
            <Link to="/forgot-password" className="anm-vergessen">Passwort vergessen?</Link>
            <div className="anm-trenner" />
            <p className="anm-grau anm-fuss">
              {/* Turnier-Bezug mitnehmen (25.10.2026): nach der Registrierung geht es direkt zur Anmeldung */}
              Noch kein Konto? <Link to={`/register${zielZusatz({ turnier: turnierZiel, event: eventZiel })}`}>Verein registrieren</Link> · <Link to="/registrieren">Als Athlet registrieren</Link>
            </p>
            <p className="anm-grau anm-fuss">Turnierpersonal bekommt den Zugang per Einladungslink vom Veranstalter.</p>
          </form>
        )}
      </div>
      <Link to="/" className="anm-zurueck">← Zur Startseite</Link>
    </div>
  );
}
